import { useEffect, useMemo } from 'react';
import type { StepProps } from '../engine/context.ts';
import { TitleBlock } from '../ui/TitleBlock.tsx';
import { Icon } from '../ui/Icon.tsx';
import { CORAL, CREAM, GREEN_BG, GREEN_FG, GREEN_TEXT, INK, LINE, MUTED, ORANGE, PAPER, SHADOW_CARD, YELLOW, MOTION } from '../ui/theme.ts';
import { reducedMotion } from '../engine/haptics.ts';

const W = 318;
const H = 140;

/** Chart (fk-fill area + fk-draw line), two stat tiles, green fit card. */
export function Projection({ step, content, ctx }: StepProps) {
  const p = ctx.projection;
  const shared = ctx.shared.projection;
  const reduced = reducedMotion();

  useEffect(() => {
    ctx.setCta({ label: content.cta ?? ctx.shared.ui?.continue ?? 'Continue', onPress: ctx.next });
    ctx.event('projection_shown', {
      factor: p.factor,
      best_from: p.tiles[0]?.from,
      best_to: p.tiles[0]?.to,
    });
    if (p.fallback) ctx.event('projection_fallback', { reason: 'missing inputs' });
  }, [step.id]);

  const path = useMemo(() => {
    const values = p.chart.length ? p.chart : [0, 1, 2, 3];
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const points = values.map((v, i) => {
      const x = (i / (values.length - 1)) * W;
      const y = H - 20 - ((v - min) / span) * (H - 40);
      return [x, y] as const;
    });
    const line = points
      .map(([x, y], i) => (i === 0 ? `M${x.toFixed(1)} ${y.toFixed(1)}` : `L${x.toFixed(1)} ${y.toFixed(1)}`))
      .join(' ');
    return { line, area: `${line} L${W} ${H} L0 ${H} Z`, last: points[points.length - 1], first: points[0] };
  }, [p.chart]);

  return (
    <>
      <TitleBlock label={content.label ?? shared.label} title={content.title} highlight={content.highlight} subtitle={content.subtitle} />

      <div style={{ flex: 'none', padding: 'var(--fk-card-pad)', borderRadius: 24, background: PAPER, boxShadow: SHADOW_CARD, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ display: 'block', overflow: 'visible' }} role="img" aria-label={`Projected reach, ${p.tiles[0]?.fromLabel} to ${p.tiles[0]?.toLabel}`}>
          <defs>
            <linearGradient id="fk-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={YELLOW} stopOpacity=".9" />
              <stop offset="1" stopColor={ORANGE} stopOpacity=".05" />
            </linearGradient>
          </defs>
          <path d={path.area} fill="url(#fk-area)" style={{ animation: reduced ? 'none' : `fk-fill ${MOTION.chartFill}ms ease-out both` }} />
          <path
            d={path.line}
            fill="none"
            stroke={CORAL}
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray="1000"
            style={{ animation: reduced ? 'none' : `fk-draw ${MOTION.chartDraw}ms ease-out both` }}
          />
          <circle cx={path.last[0]} cy={path.last[1]} r="7" fill={PAPER} stroke={CORAL} strokeWidth="4" />
          <circle cx={path.first[0]} cy={path.first[1]} r="5" fill={ORANGE} />
        </svg>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'clamp(10px, 3vw, 12px)', fontWeight: 700, color: MUTED }}>
          <span>{ctx.shared.ui?.today ?? 'Today'}</span>
          <span>{ctx.shared.ui?.days30 ?? '30 days'}</span>
          <span>{ctx.shared.ui?.days60 ?? '60 days'}</span>
          <span style={{ color: INK, fontWeight: 800 }}>{ctx.shared.ui?.days90 ?? '90 days'}</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {p.tiles.map((t, i) => (
          <div key={t.key} style={{ padding: 'var(--fk-card-pad)', borderRadius: 20, background: CREAM, border: `2px solid ${LINE}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: MUTED }}>{shared.tiles[i]?.label}</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ textDecoration: 'line-through', color: MUTED, fontWeight: 600 }}>{t.fromLabel}</span>
              <Icon name="arrow-right" size={14} color={MUTED} />
              <span style={{ fontWeight: 800, fontSize: 22 }}>{t.toLabel}</span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ padding: 'var(--fk-card-pad)', borderRadius: 20, background: GREEN_BG, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Icon name="check-circle" size={24} color={GREEN_FG} style={{ flex: 'none' }} />
        <div>
          <div style={{ fontWeight: 800, fontSize: 16 }}>{shared.fit.title}</div>
          <div className="fk-copy" style={{ fontSize: 14.5, color: GREEN_TEXT, fontWeight: 600 }}>{shared.fit.body}</div>
        </div>
      </div>

      {shared.disclaimer ? (
        <p className="fk-copy" style={{ margin: '0 auto', maxWidth: '40ch', fontSize: 13, lineHeight: 1.4, color: MUTED, fontWeight: 500, textAlign: 'center' }}>
          {shared.disclaimer}
        </p>
      ) : null}
    </>
  );
}
